import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useCommonApi } from "src/_common/hooks/actions/common/appCommonApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { hasPermission } from "src/_common/utils/permissionsUtils";
import BillingInfoModal from "src/components/modal/BillingInfoModal";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface UserData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  company?: {
    name?: string;
  };
}

interface BillingData {
  name?: string;
  phone?: string;
  business_name?: string;
  address_line_1?: string;
  country_id?: number;
  state_id?: number;
  city_id?: number;
  postal_code?: string;
  additional_email?: string;
  tax_id?: string;
  country?: string;
  state?: string;
  city?: string;
  country_details?: {
    id: number;
    name: string;
  };
  city_details?: {
    id: number;
    name: string;
  };
}

interface Country {
  id: number;
  name: string;
}

interface State {
  id: number;
  name: string;
  country_id: number;
}

interface City {
  id: number;
  name: string;
  country_id: number;
  state_id?: number;
}

interface InfoTabProps {
  userSelector?: {
    user?: UserData;
  };
}

const InfoTab: React.FC<InfoTabProps> = ({ userSelector }) => {
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();
  // const subscriptionPlanData = useAppUserSubscriptionPlanSelector();
  const userApi = useUserApi();
  const commonApi = useCommonApi();
  const toast = useToaster();
  const { t } = useTranslation("settings");

  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);

  // Location data for display names
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [locationNames, setLocationNames] = useState<{
    country?: string;
    state?: string;
    city?: string;
  }>({});

  // // Check if billing_info is enabled in the current plan
  // const isBillingInfoEnabled = subscriptionPlanData?.is_billing_info !== false; // Default to true if not specified

  // Fetch location data based on IDs
  const fetchLocationNames = async (billingInfo: BillingData) => {
    const names: { country?: string; state?: string; city?: string } = {};

    try {
      // Fetch countries
      if (billingInfo.country_id) {
        commonApi.callGetCountries(
          { limit: 99999, offset: 0 },
          (message: string, response: any) => {
            const countriesData = response?.data?.countries || response?.countries || response || [];
            setCountries(countriesData);

            const country = countriesData.find((c: Country) => c.id === billingInfo.country_id);
            if (country) {
              names.country = country.name;
              setLocationNames((prev) => ({ ...prev, country: country.name }));

              // Fetch states for this country
              if (billingInfo.state_id) {
                commonApi.callGetStates(
                  { country_id: billingInfo.country_id, limit: 999, offset: 0 },
                  (message: string, response: any) => {
                    const statesData = response?.data?.states || response?.states || response || [];
                    setStates(statesData);

                    const state = statesData.find((s: State) => s.id === billingInfo.state_id);
                    if (state) {
                      names.state = state.name;
                      setLocationNames((prev) => ({ ...prev, state: state.name }));
                    }
                  },
                  (error: any) => {
                    console.error("Failed to fetch states:", error);
                  }
                );
              }

              // Fetch cities for this country
              if (billingInfo.city_id) {
                commonApi.callGetCities(
                  { country_id: billingInfo.country_id, limit: 999999, offset: 0 },
                  (message: string, response: any) => {
                    const citiesData = response?.data?.cities || response?.cities || response || [];
                    setCities(citiesData);

                    const city = citiesData.find((c: City) => c.id === billingInfo.city_id);
                    if (city) {
                      names.city = city.name;
                      setLocationNames((prev) => ({ ...prev, city: city.name }));
                    }
                  },
                  (error: any) => {
                    console.error("Failed to fetch cities:", error);
                  }
                );
              }
            }
          },
          (error: any) => {
            console.error("Failed to fetch countries:", error);
          }
        );
      }
    } catch (error) {
      console.error("Error fetching location names:", error);
    }
  };

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === "member") {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Check permissions for different actions
  const canReadBillingInfo = canAccessMenu("read_billing_info");
  const canCreateBillingInfo = canAccessMenu("create_billing_info");
  const canEditBillingInfo = canAccessMenu("edit_billing_info");
  const canDeleteBillingInfo = canAccessMenu("delete_billing_info");

  // Fetch billing info on component mount
  useEffect(() => {
    if (canReadBillingInfo) {
      setIsLoading(true);
      userApi.callGetCompanyBillingInfo(
        (message: string, response: any) => {
          if (response?.billing_details) {
            setBillingData(response.billing_details);
            // Fetch location names for the billing data
            fetchLocationNames(response.billing_details);
          }
          setIsLoading(false);
        },
        (error: any) => {
          console.error("Failed to fetch billing info:", error);
          setIsLoading(false);
        }
      );
    }
  }, [canReadBillingInfo]);

  const handleEditClick = () => {
    setShowBillingModal(true);
  };

  const handleBillingModalSuccess = () => {
    // Refresh billing data after successful update
    setShowBillingModal(false);
    userApi.callGetCompanyBillingInfo(
      (message: string, response: any) => {
        if (response?.billing_details) {
          setBillingData(response.billing_details);
          // Fetch updated location names
          fetchLocationNames(response.billing_details);
        }
      },
      (error: any) => {
        console.error("Failed to refresh billing info:", error);
      }
    );
  };

  const handleBillingModalClose = () => {
    setShowBillingModal(false);
  };

  // If member doesn't have read permission, don't render the component
  if (userDetails?.user_type === "member" && !canReadBillingInfo) {
    return null;
  }

  return (
    <div className="billing-tab-content">
      <div className="billing-info-section">
        <div className="billing-info-header">
          <div>
            <h3>{t("billing.info.title")}</h3>
            <p className="billing-info-description">{t("billing.info.description")}</p>
          </div>
          {/* Show Edit button only if user has edit permission and billing data exists */}
          {/* {console.log('Rendering Edit button:', canEditBillingInfo, billingData)} */}
          {billingData && (
            <button className="billing-edit-btn" onClick={handleEditClick}>
              <i className="fa fa-edit"></i>
              {t("billing.info.editButton")}
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="billing-loading">{t("billing.info.loading")}</div>
        ) : billingData ? (
          <div className="billing-info-form">
            <div className="billing-form-row">
              <div className="billing-form-field">
                <label>{t("billing.info.fields.name")}</label>
                <p>{billingData.name || t("billing.info.fields.notProvided")}</p>
              </div>
              <div className="billing-form-field">
                <label>{t("billing.info.fields.phone")}</label>
                <p>{billingData.phone || t("billing.info.fields.notProvided")}</p>
              </div>
            </div>

            <div className="billing-form-row">
              <div className="billing-form-field">
                <label>{t("billing.info.fields.businessName")}</label>
                <p>{billingData.business_name || t("billing.info.fields.notProvided")}</p>
              </div>
              <div className="billing-form-field">
                <label>{t("billing.info.fields.addressLine1")}</label>
                <p>{billingData.address_line_1 || t("billing.info.fields.notProvided")}</p>
              </div>
            </div>

            <div className="billing-form-row">
              <div className="billing-form-field">
                <label>{t("billing.info.fields.country")}</label>
                <p>{billingData.country_details?.name || t("billing.info.fields.notProvided")}</p>
              </div>
              <div className="billing-form-field">
                <label>{t("billing.info.fields.city")}</label>
                <p>{billingData.city_details?.name || t("billing.info.fields.notProvided")}</p>
              </div>
            </div>

            {billingData.country_details?.name?.toLowerCase() === "uae" && (
              <div className="billing-form-row">
                <div className="billing-form-field">
                  <label>{t("billing.info.fields.taxId")}</label>
                  <p>{billingData.tax_id || t("billing.info.fields.notProvided")}</p>
                </div>
                <div className="billing-form-field">{/* Empty field for layout */}</div>
              </div>
            )}

            <div className="billing-form-row">
              <div className="billing-form-field">
                <label>{t("billing.info.fields.postalCode")}</label>
                <p>{billingData.postal_code || t("billing.info.fields.notProvided")}</p>
              </div>
              <div className="billing-form-field">
                <label>{t("billing.info.fields.additionalEmail")}</label>
                <p>{billingData.additional_email || t("billing.info.fields.notProvided")}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="billing-no-data">
            <p>{t("billing.info.noData")}</p>
          </div>
        )}
      </div>

      {/* Billing Info Modal */}
      <BillingInfoModal
        isVisible={showBillingModal}
        onClose={handleBillingModalClose}
        onSuccess={handleBillingModalSuccess}
        initialData={billingData || undefined}
      />
    </div>
  );
};

export default InfoTab;
